import { useForm } from 'hooks/useForm';
import { useNavigate } from 'react-router-dom';
import { useAddProductCategoryMutation } from '../services';
import { PRODUCT_CATEGORY_FIELDS_SCHEMA, PRODUCT_CATEGORY_INITIAL_FIELDS } from '../constants/product-category-fields';
import { FormEvent, useCallback } from 'react';
import { useAppDispatch } from 'app/hooks';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { ServerErrorType } from 'types/server-responses';
import { removeEmpty, trimValues } from 'utils/object-helpers';
import { ProductCategoryDto } from '../types/dtos';
import { PRODUCT_CATEGORY_STATUS } from '../constants/status';
import { useWorkspace } from 'hooks/useWorkspace';

const useController = () => {
    const { websiteId } = useWorkspace();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [addProductCategory, { isLoading: isAddProductCategoryLoading }] = useAddProductCategoryMutation();

    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({ defaultValues: PRODUCT_CATEGORY_INITIAL_FIELDS, validationSchema: PRODUCT_CATEGORY_FIELDS_SCHEMA });

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'productCategory.PRODUCT_CATEGORY_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = { ...formData };

        data.isActive = data.isActive === PRODUCT_CATEGORY_STATUS.ACTIVE ? true : false;
        data.websiteId = websiteId;

        return trimValues(removeEmpty(data)) as ProductCategoryDto;
    }, [formData, websiteId]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();

                    await addProductCategory(payload).unwrap();

                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [addProductCategory, getPayload, handleErrors, handleSubmit, handleSuccess, navigate],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        formData,
        onSubmit,
        onCancel,
        onChange,
        isAddProductCategoryLoading: isAddProductCategoryLoading,
        errors,
    };
};

export default useController;
